import React, { useEffect, useRef } from 'react';
import { Sparkles, X } from 'lucide-react';

const overlayStyle = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(15, 23, 42, 0.55)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 80,
  padding: 16,
};

const dialogStyle = {
  width: 'min(440px, 100%)',
  background: 'var(--color-bg-card)',
  borderRadius: 18,
  border: '1px solid var(--color-border)',
  boxShadow: '0 24px 64px rgba(0,0,0,0.35)',
  padding: '20px 22px 22px',
  position: 'relative',
};

const titleStyle = {
  margin: 0,
  fontSize: '1.2rem',
  fontWeight: 800,
  color: 'var(--color-text)',
  letterSpacing: '-0.02em',
  display: 'flex',
  alignItems: 'center',
  gap: 8,
};

const kickerStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  fontSize: 11,
  fontWeight: 800,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: 'var(--color-primary)',
  marginBottom: 6,
};

const bodyStyle = {
  marginTop: 12,
  fontSize: 14.5,
  lineHeight: 1.55,
  color: 'var(--color-text)',
};

const listStyle = {
  margin: '8px 0 14px',
  paddingLeft: 20,
  fontSize: 14,
  color: 'var(--color-text)',
  lineHeight: 1.55,
};

const actionsRowStyle = {
  display: 'flex',
  gap: 10,
  justifyContent: 'flex-end',
  marginTop: 18,
  flexWrap: 'wrap',
};

const primaryBtnStyle = {
  padding: '10px 16px',
  borderRadius: 12,
  border: 'none',
  fontWeight: 700,
  fontSize: 14,
  cursor: 'pointer',
  background: 'var(--color-primary)',
  color: '#fff',
  boxShadow: '0 4px 12px var(--color-shadow)',
};

const secondaryBtnStyle = {
  padding: '10px 16px',
  borderRadius: 12,
  border: '1px solid var(--color-border)',
  background: 'var(--color-bg)',
  color: 'var(--color-text)',
  fontWeight: 600,
  fontSize: 14,
  cursor: 'pointer',
};

const closeBtnStyle = {
  position: 'absolute',
  top: 10,
  right: 10,
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  color: 'var(--color-text)',
  opacity: 0.7,
  padding: 6,
  borderRadius: 8,
  display: 'inline-flex',
};

/**
 * Test-mode "premium unlock" dialog. Replaces a real billing flow during
 * development so the team can flip the mock entitlement and try premium UX.
 *
 * Props:
 *   open: boolean
 *   pathTitle?: string
 *   onConfirm: () => void
 *   onClose: () => void
 */
export default function PremiumUnlockModal({ open, pathTitle = '', onConfirm, onClose }) {
  const confirmRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    confirmRef.current?.focus();
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      style={overlayStyle}
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        style={dialogStyle}
        role="dialog"
        aria-modal="true"
        aria-labelledby="premium-modal-title"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          style={closeBtnStyle}
        >
          <X size={18} aria-hidden />
        </button>
        <span style={kickerStyle}>
          <Sparkles size={12} aria-hidden /> Test mode
        </span>
        <h2 id="premium-modal-title" style={titleStyle}>
          Unlock Explore Premium (test mode)
        </h2>
        <p style={bodyStyle}>
          {pathTitle ? (
            <>
              You tried to open <strong>{pathTitle}</strong>, which is part of the premium tier.
              No real payment is collected here — this is a developer test flow that flips a flag
              in your browser.
            </>
          ) : (
            <>
              No real payment is collected here. This is a developer test flow that flips a
              local flag so you can try the full premium experience end-to-end.
            </>
          )}
        </p>
        <ul style={listStyle}>
          <li>Unlocks every gated learning path on this device.</li>
          <li>Stored locally only — clearing your browser data resets it.</li>
          <li>Turn off any time in <em>Settings → Learner → Premium</em>.</li>
        </ul>
        <div style={actionsRowStyle}>
          <button type="button" style={secondaryBtnStyle} onClick={onClose}>
            Maybe later
          </button>
          <button
            ref={confirmRef}
            type="button"
            style={primaryBtnStyle}
            onClick={onConfirm}
            autoFocus
          >
            Unlock for testing
          </button>
        </div>
      </div>
    </div>
  );
}
